import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, Package, MapPin, Truck, CheckCircle2, XCircle, AlertOctagon, Search } from "lucide-react";
import { api, errorToMessage } from "@/lib/apiClient";
import { formatRam, formatStorage } from "@/lib/specs";
import { displaySerial } from "@/lib/serials";
import { INV } from "@/constants/testIds";
import StickerPrintButton from "@/components/StickerPrintButton";

const STATUS_LABEL = {
  AVAILABLE: "Available",
  RESERVED: "Reserved",
  PACKED: "Packed",
  SHIPPED: "Shipped",
  SCRAP: "Scrap",
  LOST: "Lost",
  QC_PENDING: "Pending QC",
  // V1.1 workflow labels
  PENDING_RECEIVING: "Pending Receiving",
  PENDING_SERIAL_ENTRY: "Pending Serial Entry",
  PENDING_BATTERY_DETAILS: "Pending Battery Details",
  READY_FOR_SALE: "Ready for Sale",
  SOLD: "Sold",
  RETURNED: "Returned",
  SCRAPPED: "Scrapped",
};
const STATUS_STYLES = {
  AVAILABLE: { color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  RESERVED: { color: "bg-sky-100 text-sky-800", icon: Package },
  PACKED: { color: "bg-indigo-100 text-indigo-800", icon: Package },
  SHIPPED: { color: "bg-violet-100 text-violet-800", icon: Truck },
  SCRAP: { color: "bg-rose-100 text-rose-800", icon: AlertOctagon },
  LOST: { color: "bg-amber-100 text-amber-800", icon: XCircle },
  // V1.1 workflow labels
  PENDING_RECEIVING: { color: "bg-amber-100 text-amber-800", icon: Package },
  PENDING_SERIAL_ENTRY: { color: "bg-orange-100 text-orange-800", icon: Package },
  QC_PENDING: { color: "bg-blue-100 text-blue-800", icon: Package },
  PENDING_BATTERY_DETAILS: { color: "bg-indigo-100 text-indigo-800", icon: Package },
  READY_FOR_SALE: { color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  SOLD: { color: "bg-violet-100 text-violet-800", icon: Truck },
  RETURNED: { color: "bg-yellow-100 text-yellow-800", icon: XCircle },
  SCRAPPED: { color: "bg-rose-100 text-rose-800", icon: AlertOctagon },
};

function StatusChip({ status }) {
  const s = STATUS_STYLES[status] || { color: "bg-slate-100 text-slate-700", icon: Package };
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${s.color}`}>
      <Icon size={12} /> {STATUS_LABEL[status] || status}
    </span>
  );
}

export default function InventoryPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("AVAILABLE");
  const [warehouse, setWarehouse] = useState("");
  const [rack, setRack] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);

  const params = useMemo(() => ({
    page, page_size: pageSize,
    q: q || undefined,
    status: status || undefined,
    warehouse: warehouse || undefined,
    rack: rack || undefined,
  }), [q, status, warehouse, rack, page, pageSize]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [{ data }, { data: rpt }] = await Promise.all([
        api.get("/inventory", { params }),
        api.get("/inventory/reports/register"),
      ]);
      setRows(data.items);
      setMeta(data.meta);
      setReport(rpt);
    } catch (e) {
      setError(errorToMessage(e, "Failed to load inventory"));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Inventory</h1>
          <p className="text-sm text-slate-500 mt-1">
            All laptops that have cleared QC. Each row is a Digital Passport.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Register report chip strip */}
      {report && (
        <div data-testid={INV.registerReport} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Inventory Register · Total {report.total}</div>
          <div className="flex flex-wrap gap-2">
            {report.counts.map((c) => (
              <button
                type="button"
                key={c.status}
                onClick={() => { setStatus(c.status); setPage(1); }}
                className={`rounded-full px-3 py-1 text-xs font-medium border ${
                  status === c.status
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
                }`}
              >
                {c.status} · {c.count}
              </button>
            ))}
            <button
              type="button"
              onClick={() => { setStatus(""); setPage(1); }}
              className={`rounded-full px-3 py-1 text-xs font-medium border ${
                !status
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
              }`}
            >
              ALL
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 grid grid-cols-1 sm:grid-cols-5 gap-3 shadow-sm">
        <div className="sm:col-span-2 relative">
          <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            data-testid={INV.searchInput}
            placeholder="Passport ID (RP…), serial, brand, model, warehouse, rack, shelf, bin"
            className="w-full rounded-md border border-slate-200 pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800"
          />
        </div>
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          data-testid={INV.statusFilter}
          className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800"
        >
          <option value="">All statuses</option>
          {[
            "PENDING_RECEIVING","PENDING_SERIAL_ENTRY","QC_PENDING","PENDING_BATTERY_DETAILS",
            "READY_FOR_SALE","AVAILABLE","RESERVED","PACKED","SHIPPED",
            "SOLD","RETURNED","SCRAP","SCRAPPED","LOST",
          ].map((s) => (
            <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>
          ))}
        </select>
        <input
          type="text"
          value={warehouse}
          onChange={(e) => { setWarehouse(e.target.value); setPage(1); }}
          data-testid={INV.warehouseFilter}
          placeholder="Warehouse"
          className="rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800"
        />
        <input
          type="text"
          value={rack}
          onChange={(e) => { setRack(e.target.value); setPage(1); }}
          data-testid={INV.rackFilter}
          placeholder="Rack"
          className="rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800"
        />
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm" data-testid={INV.listTable}>
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Passport</th>
              <th className="text-left px-4 py-3">Serial / Brand · Model</th>
              <th className="text-left px-4 py-3">Config</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3"><MapPin size={12} className="inline-block mr-1" /> Location</th>
              <th className="text-left px-4 py-3">QC Engineer</th>
              <th className="text-left px-4 py-3">Purchased</th>
              <th className="text-left px-4 py-3">Available since</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">No inventory rows.</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr
                key={r.passport_id}
                data-testid={INV.listRow(r.passport_id)}
                className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                onClick={() => navigate(`/admin/inventory/${r.passport_id}`)}
              >
                <td className="px-4 py-3">
                  {/* V2.3 — Universal Passport ID (RPYYMMDDNNNN) is the
                      primary business identity; internal barcode is secondary;
                      the legacy LP-… number shows as a small chip when present. */}
                  {(r.passport_uid || r.passport_no) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/admin/passports/${r.passport_id}`); }}
                      className="font-mono text-xs text-blue-700 font-semibold hover:underline"
                      title="Open Digital Passport"
                      data-testid={`dp-link-${r.passport_id}`}
                    >{r.passport_uid || r.passport_no}</button>
                  )}
                  {r.item_type && r.item_type !== "laptop" && (
                    <span className="ml-1 inline-flex items-center rounded-full bg-indigo-100 border border-indigo-200 px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wider text-indigo-800"
                          data-testid={`inv-item-type-${r.passport_id}`}>
                      {r.item_type}
                    </span>
                  )}
                  {r.legacy_passport_no && (
                    <div className="font-mono text-[9px] text-slate-400" title="Legacy Passport Number">
                      {r.legacy_passport_no}
                    </div>
                  )}
                  <div className="font-mono text-[10px] text-slate-500">{displaySerial(r.passport_number, { context: "inventory" })}</div>
                  {r.order_internal_no && (
                    <div className="font-mono text-[10px] text-slate-500">{r.order_internal_no}</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="font-mono text-xs text-slate-700">{displaySerial(r.serial_number, { context: "inventory" })}</div>
                  <div className="text-slate-900 font-medium mt-0.5">{r.brand} {r.model}</div>
                  <div className="text-xs text-slate-500">{r.processor}</div>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">
                  {(() => {
                    // V2.9.3 — Prefer the structured `spec` returned by the
                    // API. Fallback to model-string parsing for older
                    // rows / responses that don't include `spec` (full
                    // backward compatibility).
                    const it = (r.item_type || "laptop").toLowerCase();
                    if (it === "laptop") {
                      return <>{formatRam(r.ram_gb)} · {formatStorage(r.storage_gb, r.storage_type)}</>;
                    }
                    const s = r.spec || {};
                    const dash = "—";
                    const model = r.model || "";
                    const parts = [];
                    if (it === "ram") {
                      const cap = s.capacity_gb != null
                        ? `${s.capacity_gb} GB`
                        : ((model.match(/(\d+)\s*GB/i) || [])[1] ? `${model.match(/(\d+)\s*GB/i)[1]} GB` : dash);
                      const tech = s.technology
                        || ((model.match(/DDR[2-9]/i) || [])[0] || dash);
                      const freq = s.frequency_mhz != null
                        ? `${s.frequency_mhz} MHz`
                        : ((model.match(/(\d{3,5})\s*MHz/i) || [])[1] ? `${model.match(/(\d{3,5})\s*MHz/i)[1]} MHz` : dash);
                      const mod = s.module_type
                        || ((model.match(/SO-DIMM|DIMM/i) || [])[0] || dash);
                      parts.push(cap, tech, freq, mod);
                    } else if (it === "ssd") {
                      const capNum = s.capacity_gb;
                      const cap = capNum != null
                        ? (capNum >= 1000 ? `${(capNum/1024).toFixed(0)} TB` : `${capNum} GB`)
                        : (() => {
                            const m = model.match(/(\d+(?:\.\d+)?)\s*(TB|GB)/i);
                            return m ? `${m[1]} ${m[2].toUpperCase()}` : dash;
                          })();
                      const tech = s.technology
                        || ((model.match(/NVMe\s*PCIe|NVMe|M\.2\s*SATA|mSATA|SATA/i) || [])[0] || dash);
                      const form = s.form_factor
                        || ((model.match(/2\.5"|M\.2\s*\d{4}|mSATA/i) || [])[0] || dash);
                      parts.push(cap, tech, form);
                    } else if (it === "battery") {
                      const health = s.battery_health_pct != null ? `${s.battery_health_pct}%` : dash;
                      const design = s.design_capacity != null ? `${s.design_capacity} Wh` : dash;
                      parts.push(health, design);
                    } else if (it === "adapter" || it === "charger") {
                      const ct = s.charging_test_result || dash;
                      const watt = s.wattage != null
                        ? `${s.wattage} W`
                        : ((model.match(/(\d{2,4})\s*W\b/i) || [])[1] ? `${model.match(/(\d{2,4})\s*W\b/i)[1]} W` : dash);
                      parts.push(ct, watt);
                    } else {
                      parts.push(model || dash);
                    }
                    return (
                      <span className="text-slate-700 whitespace-nowrap" data-testid={`inv-spec-${r.passport_id}`}>
                        {parts.join(" | ")}
                      </span>
                    );
                  })()}
                </td>
                <td className="px-4 py-3"><StatusChip status={r.current_status} /></td>
                <td className="px-4 py-3 text-xs text-slate-700">
                  {r.current_location || <span className="text-slate-400">Unassigned</span>}
                </td>
                <td className="px-4 py-3 text-xs text-slate-700">{r.qc_engineer || "—"}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{r.purchase_date || "—"}</td>
                <td className="px-4 py-3 text-xs text-slate-600">
                  {r.available_since ? new Date(r.available_since).toLocaleDateString() : "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-slate-400">Open →</span>
                    <div onClick={(e) => e.stopPropagation()} className="inline-block">
                      <StickerPrintButton passportId={r.passport_id}
                                           source="INVENTORY" variant="compact" />
                    </div>
                    {r.item_type === "laptop" && (
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/admin/configure-laptop?passport_id=${r.passport_id}`); }}
                              title="Configure laptop"
                              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50">
                        🔧 Configure
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-center justify-between px-4 py-3 text-xs text-slate-500 border-t border-slate-100">
          <span>{meta.total} record{meta.total === 1 ? "" : "s"}</span>
          <div className="flex items-center gap-2">
            <button
              data-testid={INV.pagePrev}
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 disabled:opacity-40"
            >Prev</button>
            <span>Page {page} of {meta.total_pages || 1}</span>
            <button
              data-testid={INV.pageNext}
              disabled={page >= (meta.total_pages || 1)}
              onClick={() => setPage(page + 1)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 disabled:opacity-40"
            >Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}
